import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Zap, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { billingService, type PlanItem } from '../../services/billingService';
import { toastService } from '../../services/toastService';
import { Logger } from '../../utils/logger';

interface UpgradePlanModalProps {
  open: boolean;
  onClose: () => void;
}

export function UpgradePlanModal({ open, onClose }: UpgradePlanModalProps) {
  const { t } = useLanguage();
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [choosingPlanId, setChoosingPlanId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      Logger.debug('[UpgradePlanModal] Loading upgrade plans');
      billingService
        .getPlans()
        .then((allPlans) => {
          // Exclude free plans and agency plans
          const eligiblePlans = allPlans.filter((p) => p.name !== 'free' && !p.is_agency_plan);
          setPlans(eligiblePlans);
          Logger.info('[UpgradePlanModal] Upgrade plans loaded', { count: eligiblePlans.length });
        })
        .catch((err) => {
          Logger.error('[UpgradePlanModal] Failed to load plans', err);
          toastService.error(t.billing.toasts?.couponError ?? 'An error occurred');
        })
        .finally(() => setIsLoading(false));
    }
  }, [open, t]);

  const handleChoosePlan = async (plan: PlanItem) => {
    setChoosingPlanId(plan.id);
    try {
      Logger.info('[UpgradePlanModal] Upgrade plan selected', {
        planId: plan.id,
        billingInterval,
      });
      const result = await billingService.upgradePlan(plan.id, billingInterval);
      if (result.checkout_url) {
        // TODO: Après le retour Stripe (success_url avec ?upgraded=true), fermer automatiquement
        // la modale upgrade en écoutant le query param. Actuellement la modale reste ouverte
        // si l'utilisateur navigue en arrière sans terminer le paiement.
        window.location.href = result.checkout_url;
        return;
      }
      toastService.error(t.billing.toasts?.couponError ?? 'An error occurred');
    } catch (err) {
      Logger.error('[UpgradePlanModal] Upgrade failed', err);
      toastService.error(t.billing.toasts?.couponError ?? 'An error occurred');
    } finally {
      setChoosingPlanId(null);
    }
  };

  const formatPrice = (price: number | null) => {
    if (price == null) return '—';
    return `${price.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-lg backdrop-blur-sm"
        style={{ backgroundColor: 'rgba(var(--color-background-rgb, 255, 255, 255), 0.95)' }}
      >
        <DialogTitle className="sr-only">
          {t.billing?.upgradeTitle ?? 'Changer d\'abonnement'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t.billing?.upgradePlan ?? 'Passer à un abonnement payant'}
        </DialogDescription>

        <div className="flex flex-col items-center gap-4 py-6 px-6">
          {/* Icon */}
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(var(--color-primary-rgb, 85, 0, 255), 0.1)' }}
          >
            <Zap className="w-7 h-7" style={{ color: 'var(--color-primary)' }} />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t.billing?.upgradeTitle ?? 'Changer d\'abonnement'}
          </h3>

          {/* Billing interval toggle */}
          <div className="flex items-center gap-2 rounded-lg p-1" style={{ backgroundColor: 'var(--color-surface)' }} data-testid="upgrade-interval-toggle">
            <button
              onClick={() => setBillingInterval('monthly')}
              className="px-3 py-1.5 text-sm rounded-md transition-colors"
              style={{
                backgroundColor: billingInterval === 'monthly' ? 'var(--color-primary)' : 'transparent',
                color: billingInterval === 'monthly' ? 'white' : 'var(--color-text-secondary)',
              }}
            >
              {t.billing?.monthly ?? 'Mensuel'}
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className="px-3 py-1.5 text-sm rounded-md transition-colors"
              style={{
                backgroundColor: billingInterval === 'annual' ? 'var(--color-primary)' : 'transparent',
                color: billingInterval === 'annual' ? 'white' : 'var(--color-text-secondary)',
              }}
            >
              {t.billing?.annual ?? 'Annuel'}
            </button>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
            </div>
          )}

          {/* Plans */}
          {!isLoading && (
            <div className="w-full space-y-3">
              {plans.map((plan) => {
                const price =
                  billingInterval === 'annual' && plan.annual_subscription_price != null
                    ? plan.annual_subscription_price
                    : plan.monthly_subscription_price;
                const perMonth =
                  billingInterval === 'annual' && plan.annual_subscription_price != null
                    ? plan.annual_subscription_price / 12
                    : plan.monthly_subscription_price;

                return (
                  <div
                    key={plan.id}
                    data-testid={`upgrade-plan-card-${plan.id}`}
                    className="rounded-lg p-4"
                    style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          {plan.display_name}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                          {plan.monthly_credits.toLocaleString()} {t.quota?.aiCredits ?? 'crédits AI'} / {t.billing?.monthly?.toLowerCase() ?? 'mois'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                          {formatPrice(billingInterval === 'annual' ? perMonth : price)}
                          <span className="text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>/mo</span>
                        </p>
                        {billingInterval === 'annual' && plan.annual_subscription_price != null && (
                          <p className="text-[10px]" style={{ color: 'var(--color-text-secondary)' }}>
                            {formatPrice(plan.annual_subscription_price)}/an
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      data-testid={`upgrade-choose-${plan.id}`}
                      onClick={() => handleChoosePlan(plan)}
                      disabled={choosingPlanId != null}
                      size="sm"
                      className="w-full mt-3 flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                    >
                      {choosingPlanId === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4" />
                      )}
                      {t.billing?.choosePlan ?? 'Choisir'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Close button */}
          <Button variant="ghost" size="sm" onClick={onClose} className="mt-2">
            <X className="w-4 h-4 mr-1" />
            {t.common?.cancel ?? 'Annuler'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
